import { App } from "antd";
import { FileTextOutlined, CheckCircleFilled, FileDoneOutlined, FileSearchOutlined, FileAddOutlined } from "@ant-design/icons";
import type { FormData, ChooseProduct } from "@ifa/utils";
import { downloadOrderFormPdf } from "@ifa/utils";
import { useMemo, useState } from "react";
import CustomButton from "../../../../../../components/CustomButton";
import styles from "./FnaNeedsAnalysis.module.css";

interface FnaNeedsAnalysisProps {
  financialAnalysis?: FormData[];
  chooseProducts?: ChooseProduct[];
  sourceOrderId?: number;
}

// 购买目标映射配置
const OBJECTIVE_MAPPING = {
  A: "additionalLifeCoverageAmount",
  B: "additionalCriticalIllnessCoverageAmount",
  C: ["additionalSavingsTargetReturnPeriod", "additionalSavingsTargetReturnAmount"],
  D: ["additionalSavingsTargetReturnPeriod", "additionalSavingsTargetReturnAmount"],
  E: ["additionalInvestmentTargetReturnPeriod", "additionalInvestmentTargetReturnAmount"],
  F: "insurancePurchaseOtherObjectives",
} as const;

// 缴费期映射配置（问题4.1：缴付保费的意愿）
const PAYMENT_PERIOD_MAPPING: Record<string, string> = {
  A: "2-5年",
  B: "6-10年",
  C: "11-15年",
  D: "16-20年",
  E: "超過20年",
  F: "終身",
  G: "一次性付款不超過",
};

// 保障期映射配置（问题2：投購保單的目標得益/保障年期）
const COVERAGE_PERIOD_MAPPING: Record<string, string> = {
  A: "少於1年",
  B: "1-5年",
  C: "6-10年",
  D: "11-15年",
  E: "16-20年",
  F: "超過20年",
  G: "終身",
};

export function FnaNeedsAnalysis({ financialAnalysis, chooseProducts, sourceOrderId }: FnaNeedsAnalysisProps) {
  const { message } = App.useApp();
  const [previewing, setPreviewing] = useState(false);

  // 从 financialAnalysis 中查找指定 question 的数据
  const findFormData = (question: string): FormData | undefined => {
    return financialAnalysis?.find((item) => item.question === question);
  };

  // 获取指定 question 的第一个 item 的 value
  const getValue = (question: string): string => {
    const formData = findFormData(question);
    const value = formData?.items?.[0]?.value;
    // 支持 string 和 number 类型，转换为字符串
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    return "";
  };

  // 获取 RADIO 类型字段的选中项 key
  const getSelectedKey = (question: string): string => {
    const formData = findFormData(question);
    if (!formData?.items) return "";

    // 找到 value 为 true 的选项
    const selectedItem = formData.items.find((item) => item.value === true);
    return selectedItem?.key || "";
  };

  // 获取缴费期文本（从问题4.1：willingPremiumPaymentPeriodRange）
  const getPaymentPeriod = (): string => {
    const key = getSelectedKey("willingPremiumPaymentPeriodRange");
    if (!key) return "待完善";

    const baseText = PAYMENT_PERIOD_MAPPING[key];
    if (!baseText) return "待完善";

    // 如果选择了 G（一次性付款），需要组合金额
    if (key === "G") {
      const amount = getValue("singlePremiumPaymentLimit");
      if (amount) {
        return `${baseText} ${amount}HKD`;
      }
    }

    return baseText;
  };

  // 获取保障期文本（从问题2：policyTargetExpectedTime）
  const getCoveragePeriod = (): string => {
    const key = getSelectedKey("policyTargetExpectedTime");
    return key ? COVERAGE_PERIOD_MAPPING[key] || "待完善" : "待完善";
  };

  // 计算需要显示的购买目标标签
  const targetTags = useMemo(() => {
    const objectives = findFormData("insurancePurchaseObjectives");
    if (!objectives?.items) return [];

    const tags: Array<{ label: string; value: string }> = [];

    objectives.items.forEach((item) => {
      // 只处理 value 为 true 的选项
      if (item.value !== true || !item.key) return;

      const key = item.key as keyof typeof OBJECTIVE_MAPPING;
      const mapping = OBJECTIVE_MAPPING[key];

      if (!mapping) return;

      // 处理单值情况（A, B, F）
      if (typeof mapping === "string") {
        const value = getValue(mapping);
        if (value) {
          tags.push({
            label: key,
            value: `${value}HKD`,
          });
        }
      }
      // 处理双值情况（C, D, E）
      else if (Array.isArray(mapping)) {
        const [periodQuestion, amountQuestion] = mapping;
        const period = getValue(periodQuestion);
        const amount = getValue(amountQuestion);

        if (period && amount) {
          tags.push({
            label: key,
            value: `${period} - ${amount}HKD`,
          });
        }
      }
    });

    return tags;
  }, [financialAnalysis]);

  // 介绍的产品：所有产品
  const introducedProducts = useMemo(() => {
    return chooseProducts || [];
  }, [chooseProducts]);

  // 选择的产品：choose === true 的产品
  const selectedProducts = useMemo(() => {
    return chooseProducts?.filter((product) => product.choose === true) || [];
  }, [chooseProducts]);

  const handleViewOriginal = async () => {
    if (!sourceOrderId) {
      message.warning("無法獲取訂單資料");
      return;
    }

    try {
      setPreviewing(true);
      const result = await downloadOrderFormPdf({
        formType: "FNA",
        orderId: sourceOrderId,
      });

      // 创建 Blob URL 用于预览
      const blobUrl = URL.createObjectURL(result.blob);

      // 在新窗口中打开 PDF 预览
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        message.warning("請允許彈出窗口以預覽檔案");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      // 当窗口关闭时释放 URL
      previewWindow.addEventListener("beforeunload", () => {
        URL.revokeObjectURL(blobUrl);
      });

      message.success("正在打開預覽窗口");
    } catch (error) {
      console.error("預覽FNA文件失敗:", error);
      message.error("檔案預覽失敗，請稍後重試");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* 标题栏 */}
      <div className={styles.header}>
        <h2 className={styles.title}>最近一份財務需要分析目標概覽</h2>
        <CustomButton
          icon={<FileTextOutlined />}
          onClick={handleViewOriginal}
          loading={previewing}
          disabled={!sourceOrderId}
          className={styles.viewOriginalBtn}
          bgColor="#fff"
          color="#666"
          style={{ border: "1px solid #d9d9d9" }}
        >
          查看原始檔案
        </CustomButton>
      </div>

      <div style={{ padding: "24px" }}>
        {/* 选购产品的目标 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileAddOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>選購產品的目標</span>
          </div>
          <div className={styles.targetTags}>
            {targetTags.length > 0 ? (
              targetTags.map((tag) => (
                <div key={tag.label} className={styles.tag}>
                  <span className={styles.tagLabel}>{tag.label}</span>
                  <span className={styles.tagValue}>{tag.value}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "#94A3B8" }}>暫無數據</div>
            )}
          </div>
        </section>

        {/* 介绍的产品 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileSearchOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>介紹的產品</span>
          </div>
          <div className={styles.productList}>
            {introducedProducts.length > 0 ? (
              introducedProducts.map((product, index) => (
                <ProductCard
                  key={product.productCode || index}
                  index={index + 1}
                  name={product.productName || product.productCode || "未知产品"}
                  company={product.companyName || "待完善"}
                  paymentPeriod={getPaymentPeriod()}
                  coveragePeriod={getCoveragePeriod()}
                />
              ))
            ) : (
              <div style={{ color: "#94A3B8", padding: "16px 0" }}>暫無數據</div>
            )}
          </div>
        </section>

        {/* 选择的产品 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileDoneOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>選擇的產品</span>
          </div>
          <div className={styles.productList}>
            {selectedProducts.length > 0 ? (
              selectedProducts.map((product, index) => (
                <SelectedProductCard
                  key={product.productCode || index}
                  index={index + 1}
                  name={product.productName || product.productCode || "未知产品"}
                  company={product.companyName || "待完善"}
                  paymentPeriod={getPaymentPeriod()}
                  coveragePeriod={getCoveragePeriod()}
                />
              ))
            ) : (
              <div style={{ color: "#94A3B8", padding: "16px 0" }}>暫無數據</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// 产品卡片组件（介绍的产品）
function ProductCard({
  index,
  name,
  company,
  paymentPeriod,
  coveragePeriod,
}: {
  index: number;
  name: string;
  company: string;
  paymentPeriod: string;
  coveragePeriod: string;
}) {
  return (
    <div className={styles.productCard}>
      <div className={styles.productIndex}>{index}</div>
      <div className={styles.productInfo}>
        <div className={styles.productName}>{name}</div>
        <div className={styles.productDetails}>
          <span className={styles.companyName}>{company}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>繳費期：{paymentPeriod}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>保障期：{coveragePeriod}</span>
        </div>
      </div>
    </div>
  );
}

// 产品卡片组件（选择的产品）
function SelectedProductCard({
  index,
  name,
  company,
  paymentPeriod,
  coveragePeriod,
}: {
  index: number;
  name: string;
  company: string;
  paymentPeriod: string;
  coveragePeriod: string;
}) {
  return (
    <div className={styles.selectedProductCard}>
      <div className={styles.selectedProductIndex}>{index}</div>
      <div className={styles.productInfo}>
        <div className={styles.productName}>{name}</div>
        <div className={styles.productDetails}>
          <span className={styles.companyName}>{company}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>繳費期：{paymentPeriod}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>保障期：{coveragePeriod}</span>
        </div>
      </div>
      <CheckCircleFilled className={styles.checkIcon} />
    </div>
  );
}
